import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Col, Label, Row} from 'reactstrap';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import {getVillagesCompact} from 'app/entities/village/village.reducer';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';
import {AvField, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import {IVillage} from 'app/shared/model/village.model';
import RegistrationButtons from './registration-buttons';
import {useRegionLabels} from 'app/shared/hooks/useRegionLabels';
import {RegionDescriptionType} from 'app/shared/model/enumerations/region-description-type.model';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';

interface ISelectVillageProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  countrySettings: IMMamaSettings | null;
}

const SelectVillage: React.FC<ISelectVillageProps> = (props: ISelectVillageProps) => {
  const {compactVillages, compactVillagesLoading, patientCase, countryId, countrySettings} = props;
  const [selectedVillage, setSelectedVillage] = useState<IVillage | null>(null);
  const {getLabel} = useRegionLabels();
  const phoneMaxLength = countrySettings?.phoneNumberMaxLength || 15;

  const villageOptionList = (includeEmpty = false) => {
    const villageOptions = compactVillages.map(item => ({
      short: item.name,
      council: item.councilName,
      district: item.districtName,
      full: `${item.name} / ${item.councilName} / ${item.districtName}`,
      id: item.id,
    }));

    let result = includeEmpty ? [{short: '', full: '', id: undefined}] : [];
    result = result.concat(villageOptions);
    return result;
  };

  useEffect(() => {
    // reset the form
    props.setRegistrationData({
      healthcareFacilityId: null,
      villageId: null,
      patientCase: {
        ...patientCase,
        primaryContactName: '',
        primaryContactNumber: '',
      },
    });
  }, []);

  useEffect(() => {
    // Use the passed countryId from registration state
    if (countryId) {
      // Only fetch if we don't have villages or if the existing villages are for a different country
      if (!compactVillages || compactVillages.length === 0 || compactVillages[0]?.countryId !== countryId) {
        props.getVillagesCompact(countryId);
      }
    }
  }, [countryId]);

  useEffect(() => {

  }, []);

  const handleVillageSelection = (selectedVillageId: string) => {
    const selectedVillageData = compactVillages.find(village => village.id === selectedVillageId);
    if (selectedVillageData) {
      // Create a compatible village object for the selected village state
      const villageForState: IVillage = {
        id: selectedVillageData.id,
        name: selectedVillageData.name,
        contactName: selectedVillageData.contactName,
        phoneNumber: selectedVillageData.phoneNumber,
        council: {
          id: selectedVillageData.councilId,
          name: selectedVillageData.councilName,
          parent: {
            id: selectedVillageData.districtId,
            name: selectedVillageData.districtName
          }
        }
      };
      setSelectedVillage(villageForState);
      props.setRegistrationData({
        villageId: selectedVillageId,
        village: villageForState
      });
      if (selectedVillageData?.contactName || selectedVillageData?.phoneNumber) {
        props.setRegistrationData({
          patientCase: {
            ...patientCase,
            primaryContactName: selectedVillageData.contactName,
            primaryContactNumber: selectedVillageData.phoneNumber,
          },
        });
      }
    }
  };

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    if (selectedVillage) {
      props.setRegistrationData({patientCase: {...patientCase, [name]: value}});
    }
  };

  return (
    <div>
      <h2 className="mb-4">Where is the patient located?</h2>
      <AvForm onValidSubmit={props.handleNextStep}>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="village">Search for {getLabel(RegionDescriptionType.VILLAGE)}</Label>
              <VillageAvTypeahead
                labelKey="name"
                valueKey="id"
                data={villageOptionList(false)}
                placeholder={`Search for ${getLabel(RegionDescriptionType.VILLAGE)}`}
                id="village"
                onChange={handleVillageSelection}
                loading={compactVillagesLoading}
                input={{
                  id: 'village',
                  name: 'villageId',
                  required: true,
                }}
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="council">{getLabel(RegionDescriptionType.COUNCIL)}</Label>
              <AvInput
                type="text"
                name="council"
                id="council"
                value={selectedVillage?.council?.name || ''}
                readOnly
                placeholder={getLabel(RegionDescriptionType.COUNCIL)}
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="district">{getLabel(RegionDescriptionType.DISTRICT)}</Label>
              <AvInput
                type="text"
                name="district"
                id="district"
                value={selectedVillage?.council?.parent?.name || ''}
                readOnly
                placeholder={getLabel(RegionDescriptionType.DISTRICT)}
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="primaryContactName">Primary Contact Name ({getLabel(RegionDescriptionType.VILLAGE)} in
                charge)</Label>
              <AvField
                type="text"
                name="primaryContactName"
                id="primaryContactName"
                value={selectedVillage?.contactName || ''}
                onChange={handleContactInputChange}
                placeholder="Add the contact's full name"
                required
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="primaryContactNumber">Primary Contact Number</Label>
              <AvField
                type="number"
                name="primaryContactNumber"
                id="primaryContactNumber"
                value={selectedVillage?.phoneNumber || ''}
                onChange={handleContactInputChange}
                placeholder="Add the contact's phone number"
                required
                validate={{
                  maxLength: {value: phoneMaxLength, errorMessage: `This field should be at most ${phoneMaxLength} characters long`},
                }}
              />
            </AvGroup>
          </Col>
        </Row>
        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  compactVillages: storeState.village.compactEntities,
  compactVillagesLoading: storeState.village.compactLoading,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  patientCase: storeState.registration.entity.patientCase,
  countryId: storeState.registration.entity.countryId,
});

const mapDispatchToProps = {
  setRegistrationData,
  getVillagesCompact,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(SelectVillage);
